import TIM from 'tim-js-sdk';
// import TIM from 'tim-wx-sdk'; // 微信小程序环境请取消本行注释，并注释掉 import TIM from 'tim-js-sdk';
import TIMUploadPlugin from 'tim-upload-plugin'; // 使用前请将 IM SDK 升级到v2.9.2或更高版本
import moment from 'moment';
import { getImInfo } from '@/services/im';
import { getLocalStorage, setLocalStorage } from '@/utils/cookies';
import { scope } from '@/config/base';

export const TIMTYPE = {
  CUSTOM: 'TIMCustomElem',
  IMAGE: 'TIMImageElem',
  TEXT: 'TIMTextElem',
  SOUND: 'TIMSoundElem',
};

class BaseTim {
  private tim: any;

  static instance: any = null;

  static SDKAppID = '';

  static userId = '';

  static userSig = '';

  static tenantId = '';

  static organizeId = '';

  static bizCode = '';

  static isReady = false;

  constructor() {
    console.log('创建tim实例');
    if (BaseTim.SDKAppID) {
      this.init(BaseTim.SDKAppID);
    } else {
      const res = getLocalStorage('imInfo');
      BaseTim.SDKAppID = res.sdkAppId;
      BaseTim.userSig = res.userSig;
      BaseTim.userId = res.userId;
      BaseTim.organizeId = res.chainId;
      BaseTim.tenantId = res.tenantId;
      BaseTim.bizCode = res.bizCode;
      this.init(BaseTim.SDKAppID);
    }
  }

  /**
   * 请求im信息并获取实例
   * @returns
   */
  public static getImInfoByBaseIm() {
    return getImInfo({}).then((res) => {
      BaseTim.SDKAppID = res.sdkAppId;
      BaseTim.userSig = res.userSig;
      BaseTim.userId = res.userId;
      BaseTim.organizeId = res.chainId;
      BaseTim.tenantId = res.tenantId;
      BaseTim.bizCode = res.bizCode;
      setLocalStorage('imInfo', res);
      return BaseTim.refreshTim();
    });
  }

  /**
   * 刷新实例
   * @returns
   */
  public static refreshTim() {
    this.instance = new BaseTim();
    return this.instance;
  }

  public static getTim() {
    if (!this.instance) {
      this.instance = new BaseTim();
    }
    return this.instance;
  }

  /**
   * 销毁实例
   */
  public static destroy() {
    if (this.instance) {
      this.instance.tim.destroy();
      this.instance = null;
    }
  }

  async init(SDKAppID: string) {
    this.tim = TIM.create({ SDKAppID });
    this.tim.setLogLevel(0);
    this.tim.registerPlugin({ 'tim-upload-plugin': TIMUploadPlugin });
    this.tim.on(TIM.EVENT.SDK_READY, (event: any) => {
      console.log('---------- im 准备就绪 ----------');
      BaseTim.isReady = true;
    });
    this.login(BaseTim.userId, BaseTim.userSig);
  }

  /**
   * 定义后台所需信息
   * @param ext
   * @returns
   */
  getCloudCustomData(ext: any) {
    if (!this.tim) {
      return JSON.stringify(ext);
    }
    return JSON.stringify({
      scope,
      tenantId: BaseTim.tenantId,
      organizeId: BaseTim.organizeId,
      userId: BaseTim.userId,
      ext: '',
      ...ext,
    });
  }

  createTextMessageToGroup(text: string, groupId: string, ext = {}) {
    return this.tim.createTextMessage({
      to: groupId,
      conversationType: TIM.TYPES.CONV_GROUP,
      payload: {
        text,
      },
      cloudCustomData: this.getCloudCustomData(ext),
      // v2.18.0起支持群消息已读回执功能，如果您发消息需要已读回执，需购买旗舰版套餐，并且创建消息时将 needReadReceipt 设置为 true
      needReadReceipt: true,
    });
  }

  public createImageMessageToGroup(file: File, groupId: string, ext = {}) {
    return this.tim.createImageMessage({
      to: groupId,
      conversationType: TIM.TYPES.CONV_GROUP,
      // 消息优先级，用于群聊（v2.4.2起支持）。如果某个群的消息超过了频率限制，后台会优先下发高优先级的消息，详细请参考：https://cloud.tencent.com/document/product/269/3663#.E6.B6.88.E6.81.AF.E4.BC.98.E5.85.88.E7.BA.A7.E4.B8.8E.E9.A2.91.E7.8E.87.E6.8E.A7.E5.88.B6)
      // 支持的枚举值：TIM.TYPES.MSG_PRIORITY_HIGH, TIM.TYPES.MSG_PRIORITY_NORMAL（默认）, TIM.TYPES.MSG_PRIORITY_LOW, TIM.TYPES.MSG_PRIORITY_LOWEST
      // priority: TIM.TYPES.MSG_PRIORITY_NORMAL,
      payload: {
        file,
      },
      // 消息自定义数据（云端保存，会发送到对端，程序卸载重装后还能拉取到，v2.10.2起支持）
      cloudCustomData: JSON.stringify({
        scope,
        tenantId: BaseTim.tenantId,
        organizeId: BaseTim.organizeId,
        userId: BaseTim.userId,
        ext: '',
        ...ext,
      }),
      onProgress(event: any) {
        console.log('file uploading:', event);
      },
    });
  }

  public createTimeMessageToGroup(groupId: string, ext: any = {}) {
    return this.createCustomMessageToGroup(groupId, {
      data: moment().format('YYYY-MM-DD HH:mm:ss').toString(),
      description: 'TIMTimeElem',
      extension: JSON.stringify(ext),
    });
  }

  /**
   *  创建自定义消息
   * @param groupId
   * @param payload
   * @returns
   */
  createCustomMessageToGroup(
    groupId: string,
    payload: { data: string; description: string; extension: string },
    cloudCustomData: string = '',
  ) {
    return this.tim.createCustomMessage({
      to: groupId,
      conversationType: TIM.TYPES.CONV_GROUP,
      payload: {
        ...payload,
      },
      cloudCustomData,
      // time: new Date(item.msgTime).getTime(),
      // v2.18.0起支持群消息已读回执功能，如果您发消息需要已读回执，需购买旗舰版套餐，并且创建消息时将 needReadReceipt 设置为 true
      needReadReceipt: true,
    });
  }

  /**
   *  历史记录转聊天信息
   * @param recordList
   * @returns
   */
  recordToMessage(recordList: any[], rename = {}) {
    const theRename = { groupId: 'groupId', msgTime: 'msgTime', ...rename };
    const messageList = recordList.map((item) => {
      let newMessage = null;
      if (item.msgBody[0].msgType === 'TIMTextElem') {
        newMessage = this.tim.createTextMessage({
          to: item[theRename.groupId],
          from: item.fromAccount,
          conversationType: TIM.TYPES.CONV_GROUP,
          payload: {
            text: item.msgBody[0].msgContent.Text,
          },
          cloudCustomData: item.cloudCustomData,
          // v2.18.0起支持群消息已读回执功能，如果您发消息需要已读回执，需购买旗舰版套餐，并且创建消息时将 needReadReceipt 设置为 true
          needReadReceipt: true,
        });
      }
      if (item.msgBody[0].msgType === 'TIMSoundElem') {
        newMessage = this.tim.createCustomMessage({
          to: item[theRename.groupId],
          from: item.fromAccount,
          conversationType: TIM.TYPES.CONV_GROUP,
          payload: {
            data: JSON.stringify(item.msgBody[0].msgContent),
            description: item.msgBody[0].msgType,
            extension: item.msgBody[0].msgContent.Ext,
          },
          cloudCustomData: item.cloudCustomData,
          needReadReceipt: true,
        });
      }
      if (item.msgBody[0].msgType === 'TIMImageElem') {
        newMessage = this.tim.createCustomMessage({
          to: item[theRename.groupId],
          from: item.fromAccount,
          conversationType: TIM.TYPES.CONV_GROUP,
          payload: {
            data: item.msgBody[0].msgContent.ImageInfoArray[0].URL,
            description: item.msgBody[0].msgType,
            extension: item.msgBody[0].msgContent.Ext,
          },
          cloudCustomData: item.cloudCustomData,
          // v2.18.0起支持群消息已读回执功能，如果您发消息需要已读回执，需购买旗舰版套餐，并且创建消息时将 needReadReceipt 设置为 true
          needReadReceipt: true,
        });
      }

      if (item.msgBody[0].msgType === 'TIMCustomElem') {
        newMessage = this.tim.createCustomMessage({
          to: item[theRename.groupId],
          from: item.fromAccount,
          conversationType: TIM.TYPES.CONV_GROUP,
          payload: {
            // data: 'dice', // 用于标识该消息是骰子类型消息
            // description: String(random(1,6)), // 获取骰子点数
            // extension: '
            data: item.msgBody[0].msgContent.Data,
            description: item.msgBody[0].msgContent.Desc,
            extension: item.msgBody[0].msgContent.Ext,
          },
          cloudCustomData: item.cloudCustomData,
          // time: new Date(item.msgTime).getTime(),
          // v2.18.0起支持群消息已读回执功能，如果您发消息需要已读回执，需购买旗舰版套餐，并且创建消息时将 needReadReceipt 设置为 true
          needReadReceipt: true,
        });
      }
      newMessage.time = Date.parse(item[theRename.msgTime]) / 1000;
      return newMessage;
    });
    return messageList.reverse();
  }

  /**
   * im 登录
   * @param userID
   * @param userSig
   */
  async login(userID: string, userSig: string) {
    console.log('-----------开始登录---------');
    this.tim.login({ userID, userSig }).then((imResponse: any) => {
      console.log('登录成功返回消息===>', imResponse);
    });
  }

  getGroupTim() {
    return this.tim;
  }
}

export default BaseTim;
